import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TipCalculator = () => {
  const [tipData, setTipData] = useState({
    billAmount: '',
    tipPercentage: 15,
    numberOfPeople: 1,
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTipData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setTipData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculateTip = () => {
    const { billAmount, tipPercentage, numberOfPeople } = tipData;

    if (!billAmount || !numberOfPeople) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const bill = parseFloat(billAmount);
      const people = parseInt(numberOfPeople);
      const tipPercent = parseFloat(tipPercentage) / 100;

      if (isNaN(bill) || isNaN(people) || isNaN(tipPercent)) {
        setError('Please enter valid numbers for all fields');
        return;
      }

      if (people <= 0) {
        setError('Number of people must be at least 1');
        return;
      }

      const tipAmount = bill * tipPercent;
      const totalBill = bill + tipAmount;
      const tipPerPerson = tipAmount / people;
      const totalPerPerson = totalBill / people;

      const generateChartData = () => {
        return {
          labels: ['Original Bill', 'Tip Amount', 'Total Bill'],
          datasets: [
            {
              label: 'Bill Breakdown',
              data: [bill, tipAmount, totalBill],
              backgroundColor: [
                'rgba(75, 192, 192, 0.5)',
                'rgba(255, 99, 132, 0.5)',
                'rgba(54, 162, 235, 0.5)'
              ],
              borderColor: [
                'rgba(75, 192, 192, 1)',
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)'
              ],
              borderWidth: 1
            }
          ]
        };
      };

      setResults({
        bill,
        tipPercentage: tipPercentage,
        tipAmount: tipAmount.toFixed(2),
        totalBill: totalBill.toFixed(2),
        people: people,
        tipPerPerson: tipPerPerson.toFixed(2),
        totalPerPerson: totalPerPerson.toFixed(2)
      });
      setChartData(generateChartData());

    } catch (error) {
      setError('Please enter valid numbers for all fields');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Tip Calculator | Restaurant Bill Splitter</title>
        <meta name="description" content="Calculate restaurant tips and split bills evenly with our precision calculator. Get detailed breakdowns of tip amounts and per-person costs." />
        <meta name="keywords" content="tip calculator, bill splitter, restaurant calculator, gratuity calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-yellow-500 mr-4">💰</div>
            <h1 className="text-3xl font-bold text-gray-800">Tip Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>This calculator helps you determine appropriate tip amounts and split bills fairly. It provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Customizable tip percentages</li>
              <li>✓ Bill splitting for groups</li>
              <li>✓ Visual breakdown of costs</li>
              <li>✓ Detailed financial summary</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Bill Amount:</label>
                <input
                  type="number"
                  id="billAmount"
                  name="billAmount"
                  value={tipData.billAmount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter total bill amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tip Percentage:</label>
                <input
                  type="number"
                  id="tipPercentage"
                  name="tipPercentage"
                  value={tipData.tipPercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter tip percentage"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of People:</label>
                <input
                  type="number"
                  id="numberOfPeople"
                  name="numberOfPeople"
                  value={tipData.numberOfPeople}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter number of people"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={tipData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Show detailed analysis and cost breakdown
                </label>
              </div>
            </div>

            <button
              onClick={calculateTip}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Tip
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Tip Calculation Results</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Summary</h3>
                    <p className="text-gray-600">
                      Bill: ${results.bill.toFixed(2)}<br />
                      Tip: {results.tipPercentage}% (${results.tipAmount})<br />
                      Total: ${results.totalBill}<br />
                      People: {results.people}
                    </p>
                  </div>

                  {tipData.showDetailedAnalysis && (
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Per Person</h3>
                      <p className="text-gray-600">
                        Tip per person: ${results.tipPerPerson}<br />
                        Total per person: ${results.totalPerPerson}
                      </p>
                    </div>
                  )}
                </div>

                {chartData && tipData.showDetailedAnalysis && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Amount (USD)'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Bill Components'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Bill Breakdown',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Financial Summary</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Total Bill:</span>
                      ${results.totalBill}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Tip Amount:</span>
                      ${results.tipAmount}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Per Person:</span>
                      ${results.totalPerPerson}
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Tip Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to calculate your tip and split the bill:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Bill Amount</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the total amount of your bill</li>
                  <li>Include tax if it's not already included</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Set Tip Percentage</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose your desired tip percentage (typically 15-20%)</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Number of People</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter how many people are splitting the bill</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 4: Calculate</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Tip" to see your results</li>
                  <li>Enable detailed analysis for visual breakdown</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
            <div className="text-gray-600 space-y-4">
              <div>
                <h3 className="font-semibold">What is a standard tip percentage?</h3>
                <p>In most countries, 15-20% is considered standard for good service.</p>
              </div>
              <div>
                <h3 className="font-semibold">Should I tip on the total bill including tax?</h3>
                <p>Yes, tipping is typically calculated on the total bill including tax.</p>
              </div>
              <div>
                <h3 className="font-semibold">What if service was exceptional or poor?</h3>
                <p>Adjust your tip percentage up for exceptional service or down for poor service.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">🔢</span></h2>
            <div className="text-gray-600 bg-gray-50 p-6 rounded-lg">
              <p><strong>Inputs:</strong></p>
              <ul className="list-disc pl-6">
                <li>Bill Amount: $100</li>
                <li>Tip Percentage: 18%</li>
                <li>Number of People: 4</li>
              </ul>
              <p className="mt-4"><strong>Results:</strong></p>
              <ul className="list-disc pl-6">
                <li>Total Tip: $18.00</li>
                <li>Total Bill: $118.00</li>
                <li>Per Person: $29.50</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TipCalculator;